import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import SendIcon from '@mui/icons-material/Send'
import NextButton from '../NextButton'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import SmallModal from '../../../../components/Modals/SmallModal'
import EmployeeSelector from '../../../events/EmployeeSelector'
import { EPCRContext } from '../EPCR'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import API from '../../../../services/API'
import ShareCode from './ShareCode'

const Sharing = () => {
  const { formValues, setSelectedEPCRModule, onExit } = useContext(EPCRContext)
  const { setSiteMode, setEpcrPracticeMode } = useContext(SiteModeContext)
  const navigate = useNavigate()

  const [selectedStaffMember, setSelectedStaffMember] = useState(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const hasTreatmentLocationOwner =
    !!formValues?.review?.currentTreatmentLocation
  const treatmentLocationName =
    formValues?.review?.currentTreatmentLocation?.name || 'the treatment centre'
  const recipientName = selectedStaffMember
    ? `${selectedStaffMember.userAccount?.firstName ?? ''} ${
        selectedStaffMember.userAccount?.lastName ?? ''
      }`.trim()
    : ''

  const handleSendToAnother = async () => {
    setSending(true)
    setSendError(null)
    try {
      const res = await API.post('/epcr/transferownership', {
        epcrID: formValues._id,
        transferredTo: selectedStaffMember.userAccount._id,
      })
      if (res.status === 200) {
        setSelectedEPCRModule('')
        if (onExit) {
          onExit()
        } else {
          setSiteMode('normal')
          setEpcrPracticeMode(false)
          setShowSuccessModal(true)
        }
      } else {
        setSendError(
          res.data?.error || 'Unable to send the record. Please try again.',
        )
      }
    } catch (e) {
      setSendError('Connection lost. The record was not sent.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Stack width="100%" gap={2}>
      {hasTreatmentLocationOwner && (
        <Alert severity="warning">
          Transferring this ePCR to another staff member will remove the patient
          from {treatmentLocationName}.
        </Alert>
      )}

      <DetailsBox
        title="Send record to someone else"
        caption="Select a staff member to send this ePCR to. Unless they send it back to you, it will be locked for editing by yourself."
      >
        <Stack gap={1.5}>
          <EmployeeSelector
            value={selectedStaffMember}
            onChange={(e, value) => setSelectedStaffMember(value)}
            valueType="employee"
            includeSubcontractor
            label="Staff member"
            fullWidth
          />
          {sendError && <Alert severity="error">{sendError}</Alert>}
          {selectedStaffMember && (
            <Button
              onClick={handleSendToAnother}
              variant="contained"
              color="warning"
              startIcon={<SendIcon />}
              disabled={sending}
              sx={{ alignSelf: 'flex-start' }}
            >
              {sending ? 'Sending…' : `Send this record to ${recipientName}`}
            </Button>
          )}
        </Stack>
      </DetailsBox>

      <ShareCode ePCRID={formValues._id} />
      <NextButton newModule="Review" />
      <SmallModal
        open={showSuccessModal}
        onClose={() => {
          navigate('/epcr')
          setShowSuccessModal(false)
        }}
      />
    </Stack>
  )
}

export default Sharing
